import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Ikon,
  Iskelet,
  Kart,
  YardimGovdesi,
  cn,
  yardimKategoriIkonu,
} from "@koclukpanel/ui";
import { turYenidenBaslat, yardimAra, yardimBolumu, type YardimYazisi } from "./api";

/**
 * YARDIM BOLUMU — F-HLP-09…12
 *
 * ================================================================
 * BOS BOLUM "YARDIM YOK" DEMEK DEGIL
 * ================================================================
 * Yazilar izne gore suzuluyor. Bir kullanicinin bolumu bos
 * gorunuyorsa sebep icerigin olmamasi degil, o kullanicinin
 * gorebilecegi bir yazi olmamasidir — ekran bunu ayirt ediyor.
 *
 * ================================================================
 * YENIDEN TASARIM — UI/UX Dalga 2 (M10, mutabakat §7)
 * ================================================================
 * "Sade duz yazi" yerine: arama ustte, "Buradan baslayin" kartlari, kategori
 * kartlari (ikon + yazi sayisi; basinca yalniz o kategori), acilan yazida menu
 * yolu cipleri, numarali ADIM listesi, "Neden boyle" notu, ayri "Sik
 * karsilasilanlar" kutusu, tiklanabilir "Ilgili yazilar" ve "Ekrana git".
 * Govdeyi ciziyor `YardimGovdesi` (packages/ui; ayristirma saf ve testli).
 * Rol icin "baslangic kartlari" rolden turetilmiyor: sunucu zaten kisiye gorunen
 * yazilari donduruyor, "Ilk gun" kategorisi kime gorunuyorsa o kartlari goruyor
 * (rol adina dallanma yok).
 */
export function Yardim({
  acilanEkran,
  onEkranaGit,
  gidilebilir,
}: {
  acilanEkran?: string;
  /** "Ekrana git": yazinin anlattigi ekrani acar. Verilmezse dugme cizilmez. */
  onEkranaGit?: (ekran: string) => void;
  /** Bu kisi o ekrana gidebiliyor mu (menu izin suzgeciyle ayni olcut). */
  gidilebilir?: (ekran: string) => boolean;
}) {
  const qc = useQueryClient();
  const [sorgu, setSorgu] = useState("");
  const [acik, setAcik] = useState<string | null>(null);
  const [kategori, setKategori] = useState<string | null>(null);

  /**
   * Turu yeniden baslatma — F-HLP-04.
   *
   * Sartname "atlayan kullanici yardim bolumunden istedigi zaman
   * yeniden baslatabilir" diyor ve buranin disinda bir yer yok:
   * turu atlayan kisi onu bir daha nasil acacagini bilmek zorunda
   * kalmamali.
   */
  const yeniden = useMutation({
    mutationFn: () => turYenidenBaslat(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["tur"] }),
  });

  const bolum = useQuery({
    queryKey: ["yardim", acilanEkran ?? null],
    queryFn: () => yardimBolumu(acilanEkran),
  });

  const arama = useQuery({
    queryKey: ["yardim-ara", sorgu],
    queryFn: () => yardimAra(sorgu),
    // Bos sorguda uca hic gidilmiyor: bolum zaten elimizde.
    enabled: sorgu.trim().length > 1,
  });

  if (bolum.isPending) return <Iskelet satir={4} />;
  if (bolum.isError) {
    return (
      <BosDurum ton="hata" baslik="Yardım bölümü açılamadı" aciklama="Lütfen sayfayı yenileyin." />
    );
  }

  const araniyor = sorgu.trim().length > 1;
  const tumYazilar = [
    ...bolum.data.kategoriler.flatMap((k) => k.yazilar),
    ...bolum.data.ekranYazilari,
  ];
  const baslangic = bolum.data.kategoriler.find((k) => k.ad === "İlk gün")?.yazilar ?? [];
  const gorunenKategoriler = bolum.data.kategoriler.filter(
    (k) => kategori === null || k.ad === kategori,
  );

  /** İlgili yazı başlığından yazıya: bulunursa kategori süzgeci kalkar ve yazı açılır. */
  const yaziyaGit = (baslik: string): void => {
    const y = tumYazilar.find((x) => x.baslik === baslik);
    if (!y) return;
    setSorgu("");
    setKategori(null);
    setAcik(y.slug);
    window.setTimeout(
      () => document.getElementById(`yardim-yazi-${y.slug}`)?.scrollIntoView?.({ block: "start" }),
      0,
    );
  };
  const yazi = (y: YardimYazisi) => (
    <Yazi
      key={y.slug}
      yazi={y}
      acik={acik === y.slug}
      onAc={() => setAcik(acik === y.slug ? null : y.slug)}
      onIlgili={yaziyaGit}
      ilgiliBulunur={(b) => tumYazilar.some((x) => x.baslik === b)}
      ekranaGit={
        onEkranaGit && y.ekranAnahtari && (gidilebilir?.(y.ekranAnahtari) ?? true)
          ? () => onEkranaGit(y.ekranAnahtari!)
          : undefined
      }
    />
  );

  return (
    <div className="flex flex-col gap-5">
      <Alan
        etiket="Yardımda ara"
        value={sorgu}
        onChange={(e) => setSorgu(e.target.value)}
        placeholder="“deneme sonucu”, “şifre”, “rapor”"
      />

      {/*
        BAGLAMA DUYARLI YARDIM (F-HLP-12): kullanici bir ekrandan
        geldiyse o ekranin anlatimi EN USTTE. Aramaya baslayinca
        kayboluyor — arayan kisi baska bir sey ariyor demektir.
      */}
      {!araniyor && bolum.data.ekranYazilari.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-notr-800 text-sm font-semibold">Bulunduğunuz ekran</h3>
          {bolum.data.ekranYazilari.map(yazi)}
        </section>
      )}

      {!araniyor && baslangic.length > 0 && kategori === null && (
        <section aria-label="Buradan başlayın" className="flex flex-col gap-2">
          <h3 className="text-notr-800 text-sm font-semibold">Buradan başlayın</h3>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {baslangic.slice(0, 3).map((y) => (
              <button
                key={y.slug}
                type="button"
                onClick={() => yaziyaGit(y.baslik)}
                className="border-marka-200 bg-marka-50 hover:bg-marka-100 rounded-kart min-h-dokunma flex flex-col items-start gap-1 border p-3.5 text-left transition-colors"
              >
                <span className="text-marka-700 text-sm font-semibold">{y.baslik}</span>
                <span className="text-notr-600 text-xs">Oku →</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {!araniyor && bolum.data.kategoriler.length > 1 && (
        <section aria-label="Konular" className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-notr-800 text-sm font-semibold">Konular</h3>
            {kategori !== null && (
              <Buton gorunum="sessiz" onClick={() => setKategori(null)}>
                ← Tüm konular
              </Buton>
            )}
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {bolum.data.kategoriler.map((k) => (
              <button
                key={k.ad}
                type="button"
                aria-pressed={kategori === k.ad}
                onClick={() => setKategori(kategori === k.ad ? null : k.ad)}
                className={cn(
                  "rounded-kart min-h-dokunma flex items-center gap-3 border p-3 text-left transition-colors",
                  kategori === k.ad
                    ? "border-marka-600 bg-marka-50"
                    : "border-notr-200 bg-notr-0 hover:bg-notr-50",
                )}
              >
                <span className="bg-marka-50 text-marka-700 grid size-9 shrink-0 place-items-center rounded-[10px]">
                  <Ikon ad={yardimKategoriIkonu(k.ad)} />
                </span>
                <span className="min-w-0">
                  <span className="text-notr-900 block truncate text-sm font-semibold">{k.ad}</span>
                  <span className="text-notr-600 block text-xs">{k.yazilar.length} yazı</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {!araniyor && (
        <Kart className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-notr-700 text-sm">Panel turunu baştan izlemek ister misiniz?</p>
          <Buton gorunum="ikincil" onClick={() => yeniden.mutate()} disabled={yeniden.isPending}>
            {yeniden.isPending ? "Başlatılıyor…" : "Turu yeniden başlat"}
          </Buton>
        </Kart>
      )}

      {araniyor ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-notr-800 text-sm font-semibold">
            Arama sonuçları ({arama.data?.length ?? 0})
          </h3>
          {arama.isPending ? (
            <p className="text-notr-600 text-sm">Aranıyor…</p>
          ) : (arama.data?.length ?? 0) === 0 ? (
            /*
              "Bulunamadi" ile "yetkiniz yok" AYIRT EDILMIYOR ve bu
              bilincli: ayirt etseydik, olmayan bir sonucun aslinda
              gizlenmis oldugunu soylemis olurduk.
            */
            <p className="text-notr-600 text-sm">
              {/*
                ================================================================
                "AŞAĞIDAKİ DESTEK FORMU" DİYE BİR ŞEY YOKTU — 04.09.2026
                ================================================================
                Cümle kullanıcıyı sayfanın altına yolluyordu; orada form
                yok, hiç olmamış. Destek talebi **Talepler** ekranında
                (`yardim.destek.olustur` izni, menüde "Operasyon › Talepler").

                Aradığını bulamamış bir kullanıcıyı, olmayan bir forma
                doğru kaydırmak yardım ekranının yapabileceği en kötü şey:
                aramada başarısız olmuş kişiye ikinci bir başarısızlık
                daha yaşatıyor.
              */}
              Aramanıza uyan yazı yok. Aradığınız konu yardımda yoksa{" "}
              <strong className="text-notr-700">Talepler</strong> ekranından destek talebi
              açabilirsiniz.
            </p>
          ) : (
            arama.data!.map(yazi)
          )}
        </section>
      ) : bolum.data.toplam === 0 ? (
        <BosDurum
          baslik="Size görünen yardım yazısı yok"
          aciklama="Yazılar ekran bazında süzülür; bu, sistemde içerik olmadığı anlamına gelmez."
        />
      ) : (
        gorunenKategoriler.map((k) => (
          <section key={k.ad} className="flex flex-col gap-2">
            <h3 className="text-notr-800 flex items-center gap-2 text-sm font-semibold">
              <Ikon ad={yardimKategoriIkonu(k.ad)} boyut="kucuk" className="text-notr-600" />
              {k.ad}
            </h3>
            {k.yazilar.map(yazi)}
          </section>
        ))
      )}
    </div>
  );
}

function Yazi({
  yazi,
  acik,
  onAc,
  onIlgili,
  ilgiliBulunur,
  ekranaGit,
}: {
  yazi: YardimYazisi;
  acik: boolean;
  onAc: () => void;
  onIlgili: (baslik: string) => void;
  ilgiliBulunur: (baslik: string) => boolean;
  ekranaGit?: () => void;
}) {
  return (
    <Kart>
      <div id={`yardim-yazi-${yazi.slug}`}>
        <button
          type="button"
          onClick={onAc}
          aria-expanded={acik}
          className={cn(
            "flex w-full items-center justify-between gap-3 p-4 text-left",
            "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
          )}
        >
          <span className="text-notr-800 text-sm font-medium">{yazi.baslik}</span>
          <span aria-hidden className="text-notr-600 text-xs">
            {acik ? "−" : "+"}
          </span>
        </button>
        {acik && (
          /*
            Govde `YardimGovdesi` ile cizilir: yapili yazi (yol, adimlar, sik
            karsilasilanlar...) gorsel olur; ESKI BICIMLI duz yazi paragraflar
            olarak aynen cikar (ayristirici hicbir satiri dusurmez). Duz metne
            HTML/Markdown gucu verilmedi: yonetici panelinden girilen metne
            baglanti gomme yetkisi ayri bir is.
          */
          <div className="border-notr-200 border-t p-4">
            <YardimGovdesi
              govde={yazi.govde}
              onIlgili={onIlgili}
              ilgiliBulunur={ilgiliBulunur}
              eylem={
                ekranaGit ? (
                  <Buton gorunum="ikincil" onClick={ekranaGit}>
                    Ekrana git →
                  </Buton>
                ) : undefined
              }
            />
          </div>
        )}
      </div>
    </Kart>
  );
}
